import { Diamond, ArrowSquareOut } from 'phosphor-react';
import { Pattern } from '@/components/patterns';
import { useShouldShowAd, getMockAd } from '@/lib/ads';

interface Props {
  /** Variante visuelle. `card` = pleine largeur magazine. `compact` = item de liste numérotée. */
  variant?: 'card' | 'compact';
  /** Clé stable pour piocher la même mock-ad entre deux re-renders. */
  adKey?: string;
  className?: string;
}

/**
 * Carte publicitaire native pour la page Discover.
 * Mock-ad pour l'instant — le rendu visuel est définitif, seul le contenu est factice.
 *
 * Variant `card` : reproduit le format d'une EventCard variant="magazine" (image 16/9 + titre).
 * Variant `compact` : reproduit le format d'un item de liste numérotée "Encore X idées".
 */
export function AdNativeCard({ variant = 'card', adKey, className }: Props) {
  const visible = useShouldShowAd('discover_native');
  if (!visible) return null;
  const ad = getMockAd(adKey);

  if (variant === 'compact') {
    // Format identique à un <li> de la liste "Encore X idées"
    return (
      <li className={className}>
        <a
          href={ad.url}
          target="_blank"
          rel="noopener noreferrer sponsored"
          className="flex items-baseline gap-4 group py-2"
        >
          <span className="t-cap w-8 shrink-0 text-champagne uppercase">PUB</span>
          <div className="flex-1 min-w-0">
            <h3 className="t-ed-md leading-tight truncate group-hover:text-terre transition">
              {ad.headline}
            </h3>
            <span className="t-ui-xs text-ink-3">
              {ad.advertiser} · <span className="italic">Sponsorisé</span>
            </span>
          </div>
          <ArrowSquareOut size={14} className="text-ink-3 shrink-0 self-center" />
        </a>
      </li>
    );
  }

  // Variant card — match EventCard variant="magazine"
  return (
    <a
      href={ad.url}
      target="_blank"
      rel="noopener noreferrer sponsored"
      className={`block group ${className ?? ''}`}
    >
      {/* Visuel — fond coloré annonceur + Pattern Gazette par-dessus */}
      <div
        className="relative w-full aspect-[16/9] rounded-md overflow-hidden flex items-center justify-center"
        style={{ backgroundColor: `rgb(var(--${ad.tone}))` }}
      >
        <Pattern id={ad.pattern} opacity={0.12} />
        {/* Badge "SPONSORISÉ" en haut à gauche, comme le label catégorie sur une EventCard */}
        <span className="absolute top-3 left-3 t-cap text-paper-elev/90 drop-shadow uppercase tracking-[0.1em]">
          Sponsorisé
        </span>
        {/* Petit losange Gazette en haut à droite (signature au lieu du favori) */}
        <Diamond
          size={14}
          weight="fill"
          className="absolute top-3 right-3 text-paper-elev/80 drop-shadow"
        />
        {/* Nom de l'annonceur en gros au centre, façon affiche */}
        <span className="relative t-ed-2xl text-paper-elev text-center px-6 italic leading-tight">
          {ad.advertiser}
        </span>
      </div>

      {/* Bloc texte sous l'image — reproduit le rythme d'une EventCard magazine */}
      <div className="pt-3">
        {/* Filet champagne signature avant le nom de l'annonceur */}
        <span className="filet-court mb-2 block" />
        <div className="t-cap mb-1 text-champagne uppercase">{ad.advertiser}</div>
        <h3 className="t-ed-lg leading-tight mb-1.5 line-clamp-2 group-hover:text-terre transition">
          {ad.headline}
        </h3>
        <div className="flex items-center gap-2 text-ink-3">
          <span className="t-ui-xs italic">Annonce</span>
          <span className="text-ink-4">·</span>
          <span className="t-ui-xs">{ad.city}</span>
        </div>
      </div>
    </a>
  );
}
